import React, { forwardRef, useMemo } from "react";
import { BRAND } from "@/lib/api";
import { User } from "lucide-react";

/**
 * Figurinha de Overall do atleta — estilo card colecionável.
 * Props:
 *   - student: objeto do aluno (name, position, photo_url, id)
 *   - evaluations: lista de avaliações { date, scores: {criterion: 1..10} }
 */
export const OverallSticker = forwardRef(function OverallSticker({ student, evaluations = [] }, ref) {
  const stats = useMemo(() => {
    if (!evaluations.length) return null;
    const sorted = [...evaluations].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const last = sorted[sorted.length - 1];
    const scores = last?.scores || {};
    const entries = Object.entries(scores);
    if (!entries.length) return null;
    const total = entries.reduce((acc, [, v]) => acc + Number(v || 0), 0);
    const overall = Math.round(total); // soma das notas
    const best = entries.reduce((m, e) => (Number(e[1]) > Number(m[1]) ? e : m), entries[0]);
    const worst = entries.reduce((m, e) => (Number(e[1]) < Number(m[1]) ? e : m), entries[0]);
    return { overall, total, count: entries.length, best, worst, date: last.date };
  }, [evaluations]);

  if (!student) return null;

  // tier baseado no overall (soma das notas)
  const tier = !stats ? "none"
    : stats.overall > 100 ? "ouro"
    : stats.overall > 80 ? "prata"
    : stats.overall > 60 ? "bronze"
    : "novato";

  const tierColors = {
    ouro:    { primary: "hsl(45 100% 60%)", glow: "hsl(45 100% 55% / 0.6)", label: "OURO" },
    prata:   { primary: "hsl(210 12% 78%)", glow: "hsl(210 12% 70% / 0.55)", label: "PRATA" },
    bronze:  { primary: "hsl(25 75% 55%)", glow: "hsl(25 75% 50% / 0.55)", label: "BRONZE" },
    novato:  { primary: "hsl(213 94% 60%)", glow: "hsl(213 94% 60% / 0.5)", label: "NOVATO" },
    none:    { primary: "hsl(213 94% 60%)", glow: "hsl(213 94% 60% / 0.4)", label: "—" },
  };
  const t = tierColors[tier];

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-2xl shadow-2xl"
      style={{
        width: 300,
        minHeight: 480,
        background: "linear-gradient(155deg, hsl(218 75% 8%) 0%, hsl(218 60% 14%) 50%, hsl(218 75% 6%) 100%)",
        border: `2px solid ${t.primary}`,
        color: "hsl(48 100% 96%)",
        fontFamily: "'Barlow Condensed', sans-serif",
        boxShadow: `0 0 0 1px ${t.glow}, 0 18px 50px -8px ${t.glow}`,
      }}
      data-testid="overall-sticker"
    >
      {/* glow radial */}
      <div
        className="absolute inset-0 opacity-90 pointer-events-none"
        style={{
          background:
            `radial-gradient(circle at 100% 0%, ${t.glow} 0%, transparent 55%), radial-gradient(circle at 0% 100%, hsl(var(--vv-blue)/0.55) 0%, transparent 60%)`,
        }}
      />

      {/* faixa topo */}
      <div className="relative flex items-center justify-between px-3 py-2" style={{ background: `linear-gradient(90deg, ${t.primary}, transparent)` }}>
        <div className="flex items-center gap-2">
          <img src={BRAND.LOGO} alt="" className="h-9" style={{ filter: "drop-shadow(0 0 6px hsl(82 90% 55% / 0.6))" }} />
          <div className="text-[9px] uppercase tracking-[0.25em] font-black" style={{ color: "hsl(218 50% 10%)" }}>VÔLEI VIDA</div>
        </div>
        <div
          className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full"
          style={{
            background: "hsl(218 60% 8%)",
            color: t.primary,
            border: `1px solid ${t.primary}`,
            letterSpacing: "0.18em",
          }}
        >
          {t.label}
        </div>
      </div>

      {/* OVERALL big number */}
      <div className="relative flex items-center gap-3 px-4 pt-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: t.primary }}>Overall</div>
          <div
            className="leading-none"
            style={{
              fontFamily: "'Archivo Black', sans-serif",
              fontSize: 78,
              color: t.primary,
              textShadow: `0 0 18px ${t.glow}`,
            }}
          >
            {stats ? stats.overall : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">
            {student.position || "Sem posição"}
          </div>
        </div>

        {/* foto */}
        <div
          className="rounded-lg overflow-hidden flex-shrink-0"
          style={{
            width: 110, height: 130,
            border: `3px solid ${t.primary}`,
            boxShadow: `0 0 0 2px hsl(218 70% 8%), 0 8px 20px -6px ${t.glow}`,
          }}
        >
          {student.photo_url ? (
            <img src={student.photo_url} alt={student.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: "hsl(218 50% 18%)" }}>
              <User className="size-12 opacity-60" />
            </div>
          )}
        </div>
      </div>

      {/* nome */}
      <div className="relative px-4 mt-2">
        <div
          className="font-black uppercase leading-tight"
          style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: 22, letterSpacing: "0.01em" }}
        >
          {student.name}
        </div>
      </div>

      {/* best / worst */}
      <div className="relative px-4 mt-3 grid grid-cols-2 gap-2">
        <Stat label="Melhor" entry={stats?.best} color="hsl(82 90% 55%)" />
        <Stat label="A evoluir" entry={stats?.worst} color="hsl(0 80% 65%)" />
      </div>

      {/* footer */}
      <div
        className="relative mt-4 px-4 py-2 flex items-center justify-between text-[9px] uppercase tracking-[0.18em]"
        style={{ borderTop: `1px solid ${t.primary}55`, background: "hsl(218 70% 5% / 0.6)" }}
      >
        <span className="text-white/60">{stats?.date ? `Aval ${stats.date}` : "Sem avaliações"}</span>
        <span style={{ color: t.primary }}>#{(student.id?.slice(-4) || "0000").toUpperCase()}</span>
      </div>
    </div>
  );
});

function Stat({ label, entry, color }) {
  const name = entry?.[0] || "—";
  const value = entry?.[1] != null ? entry[1] : "—";
  return (
    <div
      className="rounded-lg px-2.5 py-1.5"
      style={{
        background: "hsl(218 45% 10% / 0.85)",
        border: `1.5px solid ${color}`,
        boxShadow: `inset 0 0 0 1px hsl(0 0% 100% / 0.04)`,
      }}
    >
      <div className="text-[8px] uppercase tracking-[0.2em] font-bold leading-none mb-0.5" style={{ color }}>
        {label}
      </div>
      <div className="flex items-end justify-between gap-2">
        <span
          className="font-bold leading-tight truncate"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, color: "hsl(48 100% 92%)" }}
          title={name}
        >
          {name}
        </span>
        <span
          className="font-black leading-none"
          style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: 18, color }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

export default OverallSticker;
